import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await login(email, password);
            navigate('/');
        } catch (err) {
            setError(err.response?.data?.detail || 'Invalid credentials');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 relative overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center opacity-15"
                style={{ backgroundImage: `url('https://images.unsplash.com/photo-1750716413444-c8a957fcf35c?w=1920')` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent" />

            <div className="relative z-10 w-full max-w-md">
                <div className="text-center mb-0">
                    <div className="flex items-center justify-center" style={{ marginBottom: '-3rem' }}>
                        <img src="/logo.png" alt="V-One Ai" className="object-contain" style={{ width: '750px', height: 'auto', mixBlendMode: 'lighten' }} />
                    </div>
                </div>

                <div className="glass-card p-8">
                    <h2 className="heading-primary text-xl text-white mb-6">SIGN IN</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Label className="text-white/60 tech-mono text-xs">EMAIL</Label>
                            <Input
                                data-testid="login-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your@email.com"
                                className="mt-1 bg-black/50 border-white/10 text-white placeholder:text-white/20 h-12 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50"
                                required
                            />
                        </div>
                        <div>
                            <Label className="text-white/60 tech-mono text-xs">PASSWORD</Label>
                            <div className="relative mt-1">
                                <Input
                                    data-testid="login-password-input"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter password"
                                    className="bg-black/50 border-white/10 text-white placeholder:text-white/20 h-12 pr-10 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        {error && (
                            <div data-testid="auth-error" className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-md p-3">
                                {error}
                            </div>
                        )}
                        <Button
                            data-testid="login-submit-btn"
                            type="submit"
                            disabled={loading}
                            className="w-full h-12 bg-cyan-500 text-black font-bold uppercase tracking-wide hover:bg-cyan-400 btn-glow"
                        >
                            {loading ? 'Processing...' : 'Sign In'}
                        </Button>
                    </form>
                </div>
                <p className="text-center text-white/20 text-xs mt-4 tech-mono">POWERED BY AI PREDICTION ENGINES</p>
            </div>
        </div>
    );
}
